/**
 * First-party acquisition attribution capture.
 *
 * Runs once on app mount.  Reads UTM / gclid / gbraid / wbraid / msclkid /
 * fbclid / ttclid + OpenAI ad click parameters from the current URL, plus
 * the referrer host and landing path.  Persists TWO layers so Google Ads
 * / GA4 attribution stays accurate even after the shopper returns
 * organically before paying:
 *
 *   * `first_touch` — snapshot captured on the FIRST visit that carried
 *     any acquisition signal.  Never overwritten within the 30-day TTL.
 *   * `latest_touch` — snapshot captured on the most recent visit that
 *     carried acquisition signals.  Overwrites on every new URL signal.
 *
 * Storage layout (localStorage + sessionStorage under `mps_attribution_v1`):
 *   {
 *     first_touch:  { utm_source, ..., gclid, ..., referrer_host, landing_path, first_touch_at, _at },
 *     latest_touch: { utm_source, ..., gclid, ..., referrer_host, landing_path, first_touch_at, _at },
 *     _at:          <ms since epoch>            // wall-clock of the last write, drives TTL
 *   }
 *
 * NO PII is captured — no name, email, address, tax IDs, medical or card
 * data.  `readAttribution()` returns a FLAT dict ready for Stripe
 * Checkout Session `metadata` after backend allow-list sanitisation.
 */

import { useEffect } from "react";

const STORAGE_KEY = "mps_attribution_v1";
const TTL_MS = 90 * 24 * 60 * 60 * 1000; // 90 days — Google Ads default cookie window

// Click-id and campaign parameter allow-list.  Everything else in the
// query string is ignored.  Kept in sync with backend `_sanitise_attribution`.
const KEYS = [
  "utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term", "utm_id",
  "gclid", "gbraid", "wbraid", "gad_source", "gad_campaignid",
  "msclkid", "fbclid", "ttclid",
  // OpenAI ad click parameters (advertiser query strings vary).
  "openai_click_id", "openai_source",
];

const readParams = () => {
  try {
    const usp = new URLSearchParams(window.location.search);
    const out = {};
    for (const k of KEYS) {
      const v = usp.get(k);
      if (v) out[k] = String(v).slice(0, 500);
    }
    return out;
  } catch (_) {
    return {};
  }
};

const readReferrer = () => {
  try {
    if (!document.referrer) return "";
    const u = new URL(document.referrer);
    // Ignore same-host referrer so we don't overwrite a real first-touch.
    if (u.host === window.location.host) return "";
    return u.host.slice(0, 200);
  } catch (_) {
    return "";
  }
};

const readStored = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || parsed._at == null) return null;
    if (Date.now() - parsed._at > TTL_MS) return null;
    return parsed;
  } catch (_) {
    return null;
  }
};

const write = (payload) => {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(payload)); } catch (_) {}
  try { sessionStorage.setItem(STORAGE_KEY, JSON.stringify(payload)); } catch (_) {}
};

const nowIso = () => new Date().toISOString();

const buildTouchSnapshot = (params, referrerHost, landingPath) => ({
  ...params,
  referrer_host:  referrerHost || "",
  landing_path:   landingPath  || "",
  first_touch_at: nowIso(),
  _at:            Date.now(),
});

/**
 * React hook — call once at the app root.  Only mutates storage when the
 * *current* page URL carries a new attribution signal, so an organic revisit
 * doesn't erase the original source.
 *
 * First-touch preservation: once written it's never overwritten within TTL.
 * Latest-touch: overwritten on every new URL signal so remarketing / mid-
 * funnel visits still get credit.
 */
export function useCaptureAttribution() {
  useEffect(() => { captureNow(); }, []);
}

/**
 * Non-hook variant of the capture routine — exported for tests and any
 * caller that needs to re-run capture outside a React tree.
 */
export function captureNow() {
  const fresh = readParams();
  const referrerHost = readReferrer();
  const landingPath = (() => {
    try { return window.location.pathname.slice(0, 200); } catch (_) { return ""; }
  })();

  const stored = readStored();
  const hasUrlSignal = Object.keys(fresh).length > 0;
  // On a brand-new visitor with no stored record we still want to capture
  // referrer_host + landing_path so organic-search attribution is preserved.
  const isBrandNew = !stored;
  if (!hasUrlSignal && !isBrandNew) return;

  const currentSnapshot = buildTouchSnapshot(fresh, referrerHost, landingPath);
  const firstTouch = stored?.first_touch || currentSnapshot;
  const latestTouch = hasUrlSignal || !stored?.latest_touch ? currentSnapshot : stored.latest_touch;

  write({
    first_touch:  firstTouch,
    latest_touch: latestTouch,
    _at: Date.now(),
  });
}

/**
 * Returns a Stripe-metadata-ready FLAT dict combining latest-touch
 * (unprefixed keys) with first-touch (`first_*` prefixed keys) for
 * reconciliation of paid orders back to their original acquisition source.
 *
 * The backend does its own sanitisation + allow-listing before writing to
 * Stripe metadata, so returning an empty object here is always safe.
 */
export function readAttribution() {
  const stored = readStored();
  if (!stored) return {};
  const out = {};

  const putScoped = (touch, prefix) => {
    if (!touch) return;
    for (const [k, v] of Object.entries(touch)) {
      if (k === "_at" || v == null) continue;
      const s = String(v).slice(0, 500);
      if (!s) continue;
      out[`${prefix}${k}`] = s;
    }
  };

  // Latest-touch takes unprefixed keys (most recent = most actionable for
  // conversion crediting), first-touch takes `first_*` prefix for audit.
  putScoped(stored.latest_touch || stored, "");
  putScoped(stored.first_touch,  "first_");

  // Hard cap — backend enforces the same ceiling with its own 25-key
  // truncation before writing to Stripe metadata. 40 here comfortably
  // fits the 16 URL-signal keys + 3 meta keys (referrer_host,
  // landing_path, first_touch_at) across BOTH the latest-touch and
  // first-touch snapshots.
  const entries = Object.entries(out).slice(0, 40);
  return Object.fromEntries(entries);
}
